import type {
  ColDef,
  RowClassParams,
  RowClickedEvent,
} from "ag-grid-community";
import "ag-grid-community/styles/ag-grid.css";
import "ag-grid-community/styles/ag-theme-alpine.css";
import type { AgGridReact as AgGridReactType } from "ag-grid-react";
import {
  Suspense,
  lazy,
  memo,
  useCallback,
  useEffect,
  useMemo,
  useRef,
} from "react";

import config from "@web-app/config";

const AgGridReactLazy = lazy(async () => {
  const [{ LicenseManager }, { AgGridReact }] = await Promise.all([
    import("ag-grid-enterprise"),
    import("ag-grid-react"),
  ]);

  LicenseManager.setLicenseKey(config.AG_GRID_LICENSE_KEY);

  return { default: AgGridReact };
});

// Type-safe wrapper for the lazy-loaded AgGrid component
const AgGridWrapper = ({
  gridRef,
  onRowClick,
  getRowClass,
  ...props
}: {
  getRowClass?: (params: RowClassParams) => string;
  gridRef: React.RefObject<AgGridReactType>;
  [key: string]: unknown;
  onRowClick?: (data: unknown) => void;
}) => {
  return (
    <AgGridReactLazy
      {...props}
      ref={gridRef}
      onRowClicked={(event: RowClickedEvent) => {
        if (event.data && onRowClick) {
          onRowClick(event.data);
        }
      }}
      getRowClass={getRowClass || (() => "cursor-pointer hover:bg-gray-50")}
    />
  );
};

interface TableProps<T> {
  className?: string;
  columnDefs: ColDef<T>[];
  getRowClass?: (params: RowClassParams<T>) => string;
  loading?: boolean;
  onRowClick?: (data: T) => void;
  pagination?: boolean;
  paginationPageSize?: number;
  paginationPageSizeSelector?: number[];
  rowData: T[];
  rowHeight?: number;
}

const Table = memo(function Table<T>({
  rowData,
  columnDefs,
  rowHeight = 70,
  onRowClick,
  className = "",
  pagination = true,
  paginationPageSize = 10,
  paginationPageSizeSelector = [10, 20, 50],
  loading = false,
  getRowClass,
  ...props
}: TableProps<T>) {
  const gridRef = useRef<AgGridReactType>(null);

  useEffect(() => {
    const api = gridRef.current?.api;
    if (!api) return;

    if (loading) {
      api.showLoadingOverlay();
    } else {
      api.hideOverlay();
    }
  }, [loading]);

  // Handle loading state with proper typing
  const tableData = loading ? (Array(5).fill({}) as T[]) : rowData;

  // Type-safe wrapper for onRowClick callback
  const handleRowClick = useCallback(
    (data: unknown) => {
      if (onRowClick) {
        onRowClick(data as T);
      }
    },
    [onRowClick],
  );

  // Type-safe wrapper for getRowClass callback
  const handleGetRowClass = useCallback(
    getRowClass
      ? (params: RowClassParams) => {
          return getRowClass(params as RowClassParams<T>);
        }
      : () => "cursor-pointer hover:bg-gray-50",
    [getRowClass],
  );

  return (
    <div className={`flex flex-col h-full ${className}`}>
      <div className="flex-1 min-h-0 ag-theme-quartz">
        <Suspense
          fallback={
            <div className="p-8 flex justify-center">
              <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-blue-500"></div>
            </div>
          }
        >
          <AgGridWrapper
            gridRef={gridRef}
            rowHeight={rowHeight}
            rowData={tableData}
            columnDefs={columnDefs}
            onRowClick={handleRowClick}
            getRowClass={handleGetRowClass}
            defaultColDef={useMemo(
              () => ({
                filter: true,
                minWidth: 100,
                sortable: true,
                suppressHeaderMenuButton: true,
              }),
              [],
            )}
            animateRows={true}
            pagination={pagination}
            paginationPageSize={paginationPageSize}
            paginationPageSizeSelector={paginationPageSizeSelector}
            suppressRowClickSelection={true}
            {...props}
          />
        </Suspense>
      </div>
    </div>
  );
}) as <T>(props: TableProps<T>) => JSX.Element;

export default Table;
